import { cva, type VariantProps } from "class-variance-authority"
import type * as React from "react"
import { cn } from "@/lib/utils"

const spinnerVariants = cva("flex items-center", {
  variants: {
    size: {
      xs: "gap-0.5",
      sm: "gap-1",
      md: "gap-1.5",
      lg: "gap-2",
    },
  },
  defaultVariants: {
    size: "md",
  },
})

const dotSize = {
  xs: "h-1 w-1",
  sm: "h-1.5 w-1.5",
  md: "h-3 w-3",
  lg: "h-4 w-4",
} as const

const textSize = {
  xs: "text-xs",
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
} as const

export interface LoadingProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof spinnerVariants> {
  text?: string
  inline?: boolean
  dotClassName?: string
  animation?: "scale" | "bounce"
}

const Loading = ({
  animation = "scale",
  className,
  dotClassName,
  size = "md",
  text,
  inline = false,
  ...props
}: LoadingProps) => {
  return (
    <div className={cn(inline ? "flex flex-row items-center gap-2" : "flex flex-col items-center", className)} {...props}>
      {inline && text && (
        <span className={cn("text-muted-foreground", textSize[size ?? "md"])}>
          {text}
        </span>
      )}
      <div className={cn(spinnerVariants({ size }))}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={cn(
              "rounded-full bg-crimson-400",
              animation === "bounce" ? "animate-bounce-dot" : "animate-scale-dot",
              dotClassName,
              dotSize[size ?? "md"]
            )}
            style={{ animationDelay: `${i * 0.2}s` }}
          />
        ))}
      </div>
      {!inline && text && (
        <span className={cn("mt-2 block text-muted-foreground", textSize[size ?? "md"])}>
          {text}
        </span>
      )}
    </div>
  )
}

/**
 * Full-page loader. Fills the shell content pane (a flex column) and centers
 * the dots, so boot, route, and page loading states stay in the same place
 * instead of painting at the top and then jumping.
 */
export function PageLoading({ className, ...props }: LoadingProps) {
  return (
    <div className={cn('flex h-full w-full flex-1 items-center justify-center', className)}>
      <Loading {...props} />
    </div>
  );
}

export default Loading;
